// @ts-nocheck
import { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/query-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Phone,
  MapPin,
  Upload,
  FileText,
  Folder,
  Users,
  MessageSquare,
  Download,
  ExternalLink,
  Trash2,
  DollarSign,
  User,
  Building,
  Eye,
  Loader2,
  AlertTriangle,
  Clock,
  CloudUpload,
  Check,
  NotebookPen,
} from "lucide-react";
import { formatDateOnly } from "@/lib/format-utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Lead } from "@shared/schema";
import { QuickAssignCard } from "@/components/claims/quick-assign-card";
import { LeadIntakeDecisionForm, LeadIntakeDecisionHistory } from "@/components/leads/lead-intake-decision";
import { ReferrerSourceCombobox } from "@/components/leads/referrer-source-combobox";
import { campaignFromSource } from "@shared/leadSourceConfig";
import { ClickToCallButton } from "@/components/calls/click-to-call-button";
import { ContactCombobox } from "@/components/contacts/contact-combobox";
import { formatEnum, PERIL_TYPES } from "@/lib/enum-utils";
import { formatPhone } from "@shared/utils/formatters";
import { LeadUploadDocDialog } from "@/components/leads/lead-upload-doc-dialog";
import { changedLeadFields } from "@/components/leads/lead-detail-utils";

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const statusColors: Record<string, string> = {
  new: "bg-emerald-500",
  contacted: "bg-blue-500",
  qualified: "bg-indigo-500",
  proposal_sent: "bg-sky-500",
  negotiating: "bg-violet-500",
  accepted: "bg-amber-500",
  rejected: "bg-red-500",
  on_hold: "bg-slate-500",
};

function EmptyValue({ text = "Not provided" }: { text?: string }) {
  return <span className="text-muted-foreground/60 italic text-sm">{text}</span>;
}

function SectionHeader({ icon: Icon, iconColor, title }: { icon: any; iconColor: string; title: string }) {
  return (
    <div className="flex items-center gap-2.5 border-l-[3px] border-primary/30 pl-3 py-0.5">
      <div className={`flex items-center justify-center h-6 w-6 rounded-md ${iconColor}`}>
        <Icon className="h-3.5 w-3.5 text-white" aria-hidden="true" />
      </div>
      <h3 className="text-sm font-semibold text-foreground tracking-wide">{title}</h3>
    </div>
  );
}

export default function LeadDetailContent() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Sync tab state with URL search params for deep-linking & back-button support
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || "overview";
  };
  const [activeTab, setActiveTabState] = useState(getInitialTab);
  const setActiveTab = useCallback((tab: string) => {
    setActiveTabState(tab);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState({}, "", url.toString());
  }, []);
  const [isEditing, setIsEditing] = useState(false);
  const [editedLead, setEditedLead] = useState<Partial<Lead>>({});
  // Snapshot of the lead taken when editing began; used to diff the form so we
  // only PATCH fields the user actually changed (never stale notes, etc.).
  const [editSnapshot, setEditSnapshot] = useState<Partial<Lead>>({});
  const [localNotes, setLocalNotes] = useState<string>('');
  const [_selectedAdjusterDeprecated, _setSelectedAdjusterDeprecated] = useState<string>("");
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [conversionFileType, setConversionFileType] = useState<"public_adjuster" | "appraisal" | "estimate" | "umpire" | "other">("public_adjuster");
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // Handle edit mode from URL query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('edit') === 'true') {
      setIsEditing(true);
      // Remove the edit param from URL to prevent re-triggering
      window.history.replaceState({}, '', `/leads/${id}`);
    }
  }, [id]);


  // Fetch lead details
  const { data: lead, isLoading } = useQuery<Lead>({
    queryKey: [`/api/leads/${id}`],
    enabled: !!id,
  });

  // Fetch lead files
  const { data: files } = useQuery({
    queryKey: [`/api/leads/${id}/documents`],
    enabled: !!id,
  });

  // Fetch lead activity
  const { data: activity } = useQuery({
    queryKey: [`/api/leads/${id}/activity`],
    enabled: !!id,
  });

  // Initialize editedLead when lead data loads while in edit mode
  useEffect(() => {
    if (isEditing && lead && Object.keys(editedLead).length === 0) {
      setEditedLead(lead);
      setEditSnapshot(lead);
    }
  }, [isEditing, lead]);

  // Sync localNotes from lead query result
  useEffect(() => {
    if (lead) {
      setLocalNotes(lead.notes ?? '');
    }
  }, [lead]);

  // Fetch all adjusters for assignment
  const { data: allAdjusters = [] } = useQuery({
    queryKey: ['/api/users/adjusters'],
    queryFn: async () => {
      const res = await fetch('/api/users/adjusters');
      if (!res.ok) throw new Error('Failed to fetch adjusters');
      return res.json();
    },
    enabled: !!id,
  });

  // Fetch existing lead PA assignments
  const { data: leadPaAssignmentsData = [] } = useQuery({
    queryKey: [`/api/leads/${id}/pa-assignments`],
    enabled: !!id,
  });

  // Update lead mutation
  const updateLeadMutation = useMutation({
    mutationFn: async (updates: Partial<Lead>) => {
      return await apiRequest('PATCH', `/api/leads/${id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/leads/${id}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/leads'] });
      toast({
        title: "Success",
        description: "Lead details updated successfully",
      });
      setIsEditing(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update lead details",
        variant: "destructive",
      });
    },
  });

  // Convert to claim mutation
  const convertToClaimMutation = useMutation({
    mutationFn: async (fileType: "public_adjuster" | "appraisal" | "estimate" | "umpire" | "other") => {
      const res = await apiRequest('POST', `/api/leads/${id}/convert`, { fileType });
      return await res.json();
    },
    onSuccess: (newClaim: any) => {
      queryClient.invalidateQueries({ queryKey: [`/api/leads/${id}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/claims'] });
      
      const fileTypeLabels: Record<string, string> = { public_adjuster: "PA file", appraisal: "appraisal file", estimate: "estimate file", umpire: "umpire file", other: "claim file" };
      const fileTypeLabel = fileTypeLabels[newClaim.fileType] || "claim file";
      toast({
        title: "Lead Converted",
        description: `Successfully converted to ${fileTypeLabel}`,
      });
      setConvertDialogOpen(false);
      navigate(`/claims/${newClaim.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Conversion Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // File delete mutation
  const [fileToDelete, setFileToDelete] = useState<any>(null);

  const deleteFileMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const response = await apiRequest('DELETE', `/api/documents/${documentId}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/leads/${id}/documents`] });
      setFileToDelete(null);
      toast({ title: "Success", description: "File deleted successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete file", variant: "destructive" });
    },
  });

  // File re-upload mutation
  const reuploadFileMutation = useMutation({
    mutationFn: async ({ documentId, file }: { documentId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiRequest('PUT', `/api/leads/${id}/documents/${documentId}/reupload`, formData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/leads/${id}/documents`] });
      toast({ title: "Success", description: "File re-uploaded successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to re-upload file", variant: "destructive" });
    },
  });

  // PA assignment mutations
  const createPaAssignmentMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', `/api/leads/${id}/pa-assignments`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/leads/${id}/pa-assignments`] });
      toast({ title: "Success", description: "Sales PA assigned" });
    },
    onError: () => toast({ title: "Error", description: "Failed to assign Sales PA", variant: "destructive" }),
  });

  const updatePaAssignmentMutation = useMutation({
    mutationFn: async ({ data }: { data: any }) => {
      const res = await apiRequest('PATCH', `/api/leads/${id}/pa-assignments/${data.assignmentId}`, data);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [`/api/leads/${id}/pa-assignments`] }),
    onError: () => toast({ title: "Error", description: "Failed to update assignment", variant: "destructive" }),
  });

  const deletePaAssignmentMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      await apiRequest('DELETE', `/api/leads/${id}/pa-assignments/${assignmentId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [`/api/leads/${id}/pa-assignments`] }),
    onError: () => toast({ title: "Error", description: "Failed to remove assignment", variant: "destructive" }),
  });

  const submitFeedbackMutation = useMutation({
    mutationFn: async (outcome: string) => {
      const response = await apiRequest('POST', `/api/leads/${id}/feedback`, { outcome });
      return response.json();
    }
  });

  const handleStatusChange = (newStatus: string) => {
    if (!lead || newStatus === lead.status) return;
    if (newStatus === "accepted") {
      setConversionFileType("public_adjuster");
      setConvertDialogOpen(true);
    } else if (newStatus === "rejected") {
      updateLeadMutation.mutate({ status: "rejected" });
      submitFeedbackMutation.mutate("declined");
    } else {
      updateLeadMutation.mutate({ status: newStatus });
    }
  };

  const handleSaveChanges = () => {
    const changes = changedLeadFields(editSnapshot, editedLead);
    if (Object.keys(changes).length === 0) {
      setIsEditing(false);
      setEditedLead({});
      setEditSnapshot({});
      return;
    }
    updateLeadMutation.mutate(changes);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-3 animate-in fade-in duration-500">
        <Loader2 className="h-8 w-8 animate-spin text-primary/60" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Loading lead details\u2026</p>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="flex items-center justify-center h-96">Lead not found</div>
    );
  }

  return (
    <>
    <div className="flex flex-col gap-6">
        {/* Header — Hero-style */}
        <div className="relative rounded-xl border bg-card overflow-hidden">
          {/* Subtle gradient accent strip */}
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-primary/70 to-primary/30" />
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0 space-y-2">
                <h1 className="text-3xl font-bold tracking-tight" style={{ textWrap: 'balance' }}>
                  {lead.claimType === 'commercial' ? (lead.company || 'Commercial Lead') : `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead'}
                </h1>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                    Updated {dateFormatter.format(new Date(lead.updatedAt || lead.createdAt))}
                  </span>
                  {lead.estimatedValue && (
                    <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                      <DollarSign className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                      {currencyFormatter.format(parseFloat(lead.estimatedValue))}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                <Select value={lead.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="h-8 font-medium border shadow-none bg-transparent hover:bg-muted text-foreground focus:ring-0 focus:ring-offset-0 ring-offset-0" data-testid="select-lead-status">
                    <div className="flex items-center gap-2 pr-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${statusColors[lead.status] || 'bg-slate-400'}`} />
                      <SelectValue placeholder="Select status..." />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {['new', 'contacted', 'qualified', 'proposal_sent', 'negotiating', 'accepted', 'rejected', 'on_hold'].map(s => (
                      <SelectItem key={s} value={s} className="font-medium">
                        <div className="flex items-center gap-2">
                          {formatEnum(s)}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
        <TabsList className="grid w-full max-w-lg grid-cols-4">
          <TabsTrigger value="overview" className="gap-1.5">📄 Overview</TabsTrigger>
          <TabsTrigger value="intake" data-testid="tab-intake" className="gap-1.5">
            💡 Intake
          </TabsTrigger>
          <TabsTrigger value="files" className="gap-1.5">📁 Files</TabsTrigger>
          <TabsTrigger value="notes" className="gap-1.5">
            <NotebookPen className="h-3.5 w-3.5" />
            Notes
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>
                  General information about the lead and additional details
                </CardDescription>
              </div>
              {!isEditing && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(true);
                    setEditedLead(lead);
                    setEditSnapshot(lead);
                  }}
                  data-testid="button-edit"
                >
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Contact Info Section */}
              <div className="space-y-4">
                <SectionHeader icon={User} iconColor="bg-blue-600" title="Contact Info" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {lead.claimType === 'commercial' ? (
                    <>
                      <div className="md:col-span-2 space-y-1.5">
                        <Label htmlFor="company" className="text-xs text-muted-foreground">Business/Company Name</Label>
                        {isEditing ? (
                          <Input
                            id="company"
                            value={editedLead.company || ''}
                            onChange={(e) => setEditedLead({ ...editedLead, company: e.target.value })}
                            autocomplete="organization"
                            data-testid="input-company"
                          />
                        ) : (
                          <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-company">{lead.company || <EmptyValue />}</p>
                        )}
                      </div>
                      <div className="md:col-span-2 space-y-1.5">
                        <Label htmlFor="pointOfContact" className="text-xs text-muted-foreground">Point of Contact</Label>
                        {isEditing ? (
                          <Input
                            id="pointOfContact"
                            value={editedLead.pointOfContact || ''}
                            onChange={(e) => setEditedLead({ ...editedLead, pointOfContact: e.target.value })}
                            autocomplete="off"
                            data-testid="input-point-of-contact"
                          />
                        ) : (
                          <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-point-of-contact">{lead.pointOfContact || <EmptyValue />}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-1.5">
                        <Label htmlFor="firstName" className="text-xs text-muted-foreground">First Name</Label>
                        {isEditing ? (
                          <Input
                            id="firstName"
                            value={editedLead.firstName}
                            onChange={(e) => setEditedLead({ ...editedLead, firstName: e.target.value })}
                            autocomplete="given-name"
                            data-testid="input-firstname"
                          />
                        ) : (
                          <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-firstname">{lead.firstName || <EmptyValue />}</p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="lastName" className="text-xs text-muted-foreground">Last Name</Label>
                        {isEditing ? (
                          <Input
                            id="lastName"
                            value={editedLead.lastName}
                            onChange={(e) => setEditedLead({ ...editedLead, lastName: e.target.value })}
                            autocomplete="family-name"
                            data-testid="input-lastname"
                          />
                        ) : (
                          <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-lastname">{lead.lastName || <EmptyValue />}</p>
                        )}
                      </div>
                    </>
                  )}
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-xs text-muted-foreground">Phone</Label>
                    {isEditing ? (
                      <PhoneInput
                        id="phone"
                        value={editedLead.phone || ''}
                        onChange={(v) => setEditedLead({ ...editedLead, phone: v })}
                        data-testid="input-phone"
                      />
                    ) : (
                      <div className="flex items-center -ml-2 text-sm">
                        {lead.phone ? (
                          <ClickToCallButton
                            phoneNumber={lead.phone}
                            contactName={`${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead'}
                            customLabel={formatPhone(lead.phone)}
                            variant="ghost"
                            className="text-primary hover:underline font-medium py-2 px-3 h-auto w-full justify-start text-left bg-muted/50 hover:bg-muted/80 rounded-md"
                            fallbackComponent={<p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50 w-full" data-testid="input-phone">{formatPhone(lead.phone)}</p>}
                          />
                        ) : (
                          <p className="text-sm font-medium py-2 px-3 ml-2 rounded-md bg-muted/50 w-full" data-testid="input-phone"><EmptyValue /></p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs text-muted-foreground">Email</Label>
                    {isEditing ? (
                      <Input
                        id="email"
                        type="email"
                        value={editedLead.email}
                        onChange={(e) => setEditedLead({ ...editedLead, email: e.target.value })}
                        autocomplete="email"
                        spellCheck={false}
                        data-testid="input-email"
                      />
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50 truncate" data-testid="input-email">{lead.email || <EmptyValue />}</p>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Address Section */}
              <div className="space-y-4">
                <SectionHeader icon={MapPin} iconColor="bg-violet-600" title="Property Address" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-1.5">
                    <Label htmlFor="propertyAddress" className="text-xs text-muted-foreground">Street Address</Label>
                    {isEditing ? (
                      <Input
                        id="propertyAddress"
                        value={editedLead.propertyAddress}
                        onChange={(e) => setEditedLead({ ...editedLead, propertyAddress: e.target.value })}
                        autocomplete="street-address"
                        data-testid="input-address"
                      />
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-address">{lead.propertyAddress || <EmptyValue />}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="propertyCity" className="text-xs text-muted-foreground">City</Label>
                    {isEditing ? (
                      <Input
                        id="propertyCity"
                        value={editedLead.propertyCity}
                        onChange={(e) => setEditedLead({ ...editedLead, propertyCity: e.target.value })}
                        autocomplete="address-level2"
                        data-testid="input-city"
                      />
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-city">{lead.propertyCity || <EmptyValue />}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="propertyState" className="text-xs text-muted-foreground">State</Label>
                    {isEditing ? (
                      <Input
                        id="propertyState"
                        value={editedLead.propertyState}
                        onChange={(e) => setEditedLead({ ...editedLead, propertyState: e.target.value })}
                        autocomplete="address-level1"
                        data-testid="input-state"
                      />
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-state">{lead.propertyState || <EmptyValue />}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="propertyZip" className="text-xs text-muted-foreground">Zip Code</Label>
                    {isEditing ? (
                      <Input
                        id="propertyZip"
                        value={editedLead.propertyZip}
                        onChange={(e) => setEditedLead({ ...editedLead, propertyZip: e.target.value })}
                        autocomplete="postal-code"
                        data-testid="input-zip"
                      />
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-zip">{lead.propertyZip || <EmptyValue />}</p>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Loss Details Section */}
              <div className="space-y-4">
                <SectionHeader icon={AlertTriangle} iconColor="bg-amber-600" title="Loss Details" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="perilType" className="text-xs text-muted-foreground">Peril Type</Label>
                    {isEditing ? (
                      <Select
                        value={editedLead.perilType || ''}
                        onValueChange={(value) => setEditedLead({ ...editedLead, perilType: value })}
                      >
                        <SelectTrigger id="perilType" data-testid="input-peril">
                          <SelectValue placeholder="Select peril type\u2026" />
                        </SelectTrigger>
                        <SelectContent>
                          {PERIL_TYPES.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-peril">{formatEnum(lead.perilType)}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lossDate" className="text-xs text-muted-foreground">Loss Date</Label>
                    {isEditing ? (
                      <Input
                        id="lossDate"
                        type="date"
                        value={editedLead.dateOfLoss ? String(editedLead.dateOfLoss).substring(0, 10) : ''}
                        onChange={(e) => setEditedLead({ ...editedLead, dateOfLoss: e.target.value })}
                        max={new Date().toISOString().split('T')[0]}
                        data-testid="input-lossdate"
                      />
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-lossdate">
                        {lead.dateOfLoss ? formatDateOnly(lead.dateOfLoss) : '—'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Campaign */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="campaign" className="text-xs text-muted-foreground">Campaign</Label>
                    {isEditing ? (
                      <Select
                        value={editedLead.campaign || ''}
                        onValueChange={(value) => setEditedLead({ ...editedLead, campaign: value })}
                      >
                        <SelectTrigger id="campaign" data-testid="select-campaign">
                          <SelectValue placeholder="Select campaign..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="solar">Solar</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="select-campaign">{lead.campaign ? formatEnum(lead.campaign) : <EmptyValue />}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="source" className="text-xs text-muted-foreground">Source</Label>
                    {isEditing ? (
                      <Select
                        value={editedLead.source || ''}
                        onValueChange={(value) => setEditedLead({ ...editedLead, source: value as any })}
                      >
                        <SelectTrigger id="source" data-testid="select-source">
                          <SelectValue placeholder="Select source..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="referral">Referral</SelectItem>
                          <SelectItem value="website">Website</SelectItem>
                          <SelectItem value="phone_call">Phone Call</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="social_media">Social Media</SelectItem>
                          <SelectItem value="advertising">Advertising</SelectItem>
                          <SelectItem value="networking">Networking</SelectItem>
                          <SelectItem value="existing_client">Existing Client</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                          <SelectItem value="ai_search">AI Search</SelectItem>
                          <SelectItem value="google_search">Google Search</SelectItem>
                          <SelectItem value="sponsored_ad">Sponsored Ad</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="select-source">
                        {lead.source ? formatEnum(lead.source) : <EmptyValue />}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="referrerSource" className="text-xs text-muted-foreground">Referrer Source</Label>
                {isEditing ? (
                  <ReferrerSourceCombobox
                    value={editedLead.referrerSource ?? lead.referrerSource ?? null}
                    onChange={(val) => {
                      const currentCampaign = editedLead.campaign ?? lead.campaign;
                      const updates: Partial<Lead> = { ...editedLead, referrerSource: val };
                      if (!currentCampaign && val) {
                        const derived = campaignFromSource(val);
                        if (derived) updates.campaign = derived as typeof lead.campaign;
                      }
                      setEditedLead(updates);
                    }}
                  />
                ) : (
                  <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-referrer-source">
                    {lead.referrerSource || <EmptyValue />}
                  </p>
                )}
              </div>

              <Separator />

              {/* Referral Source Section */}
              <div className="space-y-4">
                <SectionHeader icon={Users} iconColor="bg-teal-600" title="Referral Source" />
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Referrer</Label>
                  {isEditing ? (
                    <ContactCombobox
                      type="contractor"
                      value={editedLead.referrerId ?? lead.referrerId ?? null}
                      onSelect={(contact) => setEditedLead({ ...editedLead, referrerId: contact?.id || null })}
                      allowCreate
                      placeholder="Search or create referrer..."
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Name</Label>
                        <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-referred-by">
                          {lead.referrer ? ([(lead.referrer as any).firstName, (lead.referrer as any).lastName].filter(Boolean).join(' ') || (lead.referrer as any).company) : null || <EmptyValue />}
                        </p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Company</Label>
                        <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50" data-testid="input-referred-by-company">{(lead.referrer as any)?.company || <EmptyValue />}</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Email</Label>
                        <p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50 truncate" data-testid="input-referred-by-email">{(lead.referrer as any)?.email || <EmptyValue />}</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Phone</Label>
                        <div className="flex items-center -ml-2 text-sm">
                          {(lead.referrer as any)?.phone ? (
                            <ClickToCallButton
                              phoneNumber={(lead.referrer as any).phone}
                              contactName={[(lead.referrer as any).firstName, (lead.referrer as any).lastName].filter(Boolean).join(' ') || 'Referrer'}
                              customLabel={formatPhone((lead.referrer as any).phone)}
                              variant="ghost"
                              className="text-primary hover:underline font-medium py-2 px-3 h-auto w-full justify-start text-left bg-muted/50 hover:bg-muted/80 rounded-md"
                              fallbackComponent={<p className="text-sm font-medium py-2 px-3 rounded-md bg-muted/50 w-full" data-testid="input-referred-by-phone">{formatPhone((lead.referrer as any).phone)}</p>}
                            />
                          ) : (
                            <p className="text-sm font-medium py-2 px-3 ml-2 rounded-md bg-muted/50 w-full" data-testid="input-referred-by-phone"><EmptyValue /></p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Notes Section (read-only; edit on the Notes tab) */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <SectionHeader icon={NotebookPen} iconColor="bg-rose-600" title="Notes" />
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => setActiveTab("notes")}
                    data-testid="button-view-notes-tab"
                  >
                    Edit in Notes tab
                  </Button>
                </div>
                {lead.notes ? (
                  <p
                    className="text-sm whitespace-pre-wrap py-2 px-3 rounded-md bg-muted/50"
                    data-testid="text-lead-notes"
                  >
                    {lead.notes}
                  </p>
                ) : (
                  <p className="py-2 px-3" data-testid="text-lead-notes">
                    <EmptyValue text="No notes yet" />
                  </p>
                )}
              </div>

              {isEditing && (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedLead({});
                      setEditSnapshot({});
                    }}
                    data-testid="button-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={updateLeadMutation.isPending}
                    data-testid="button-save"
                  >
                    Save Changes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Intake Decision Tab */}
        <TabsContent value="intake" className="space-y-6">
          <LeadIntakeDecisionHistory leadId={id!} />
          <LeadIntakeDecisionForm 
            leadId={id!} 
            lead={lead}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["/api/intake-decisions/lead", id] });
            }}
          />
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>All Files</CardTitle>
              <CardDescription>
                Documents associated with this lead. These files can be migrated to the claim if you convert the lead.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  {/* <Button variant="outline" size="sm">
                    <Folder className="h-4 w-4 mr-2" />
                    Add Folder
                  </Button> */}
                </div>
                <div>
                  <Button
                    onClick={() => setUploadDialogOpen(true)}
                    data-testid="button-upload"
                  >
                    <Upload className="h-4 w-4 mr-2" aria-hidden="true" />
                    Upload Files
                  </Button>
                  <LeadUploadDocDialog
                    open={uploadDialogOpen}
                    onOpenChange={setUploadDialogOpen}
                    leadId={id}
                  />
                </div>
              </div>

              <Separator />

              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {files && files.length > 0 ? (
                    files.map((file: any) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/70 transition-all duration-150 hover:shadow-sm"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                          <div>
                            <p className="font-medium">{file.fileName || file.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {file.uploadedAt ? `Uploaded ${dateFormatter.format(new Date(file.uploadedAt))}` : 'Recently uploaded'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {file.needsReupload ? (
                            <>
                              <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 text-xs dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/25">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Needs re-upload
                              </Badge>
                              <Input
                                type="file"
                                id={`reupload-${file.id}`}
                                className="hidden"
                                onChange={(e) => {
                                  const newFile = e.target.files?.[0];
                                  if (newFile) reuploadFileMutation.mutate({ documentId: file.id, file: newFile });
                                  e.target.value = '';
                                }}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.eml"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => document.getElementById(`reupload-${file.id}`)?.click()}
                                disabled={reuploadFileMutation.isPending}
                              >
                                <Upload className="h-4 w-4 mr-1" />
                                Re-upload
                              </Button>
                            </>
                          ) : (
                            <>
                              {/^(image\/|application\/pdf)/.test(file.fileType || '') && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Preview file"
                                  aria-label="Preview file"
                                  onClick={() => {
                                    setIsPdfLoading(true);
                                    setPreviewFile(file);
                                    fetch(`/api/documents/${file.id}/download?inline=true`, { method: 'HEAD' })
                                      .then((res) => {
                                        if (!res.ok) {
                                          setIsPdfLoading(false);
                                          setPreviewFile(current => current?.id === file.id ? null : current);
                                          toast({ title: "Preview unavailable", description: "This file could not be loaded for preview.", variant: "destructive" });
                                        }
                                      })
                                      .catch(() => {
                                        setIsPdfLoading(false);
                                        setPreviewFile(current => current?.id === file.id ? null : current);
                                        toast({ title: "Preview unavailable", description: "This file could not be loaded for preview.", variant: "destructive" });
                                      });
                                  }}
                                  data-testid={`button-preview-${file.id}`}
                                >
                                  <Eye className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Open in new tab"
                                aria-label="Open in new tab"
                                onClick={() => window.open(`/api/documents/${file.id}/download?inline=true`, '_blank', 'noopener,noreferrer')}
                                data-testid={`button-open-tab-${file.id}`}
                              >
                                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                              </Button>
                              {file.driveWebViewLink && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Open in Google Drive"
                                  onClick={() => window.open(file.driveWebViewLink, '_blank')}
                                >
                                  <Download className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              )}
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Delete file"
                            aria-label="Delete file"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setFileToDelete(file)}
                            data-testid={`button-delete-${file.id}`}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-4">
                      <div className="h-16 w-16 rounded-2xl bg-muted/80 flex items-center justify-center">
                        <CloudUpload className="h-8 w-8 text-muted-foreground/50" aria-hidden="true" />
                      </div>
                      <div className="text-center space-y-1">
                        <p className="font-medium text-foreground/70">No files uploaded yet</p>
                        <p className="text-sm">Upload documents to attach them to this lead</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUploadDialogOpen(true)}
                        className="mt-1"
                      >
                        <Upload className="h-4 w-4 mr-1.5" aria-hidden="true" />
                        Upload First File
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>

              <AlertDialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete File</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{fileToDelete?.fileName || fileToDelete?.name}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => fileToDelete && deleteFileMutation.mutate(fileToDelete.id)}
                      disabled={deleteFileMutation.isPending}
                    >
                      {deleteFileMutation.isPending ? "Deleting..." : "Delete"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assigned Tab */}
        <TabsContent value="assigned" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Assigned Personnel</CardTitle>
              <CardDescription>
                Manage the personnel assigned to this lead. This will optionally be migrated to the claim if you convert the lead.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs defaultValue="team" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="team">
                    <Users className="h-4 w-4 mr-2" />
                    Team
                  </TabsTrigger>
                  <TabsTrigger value="external">
                    <Building className="h-4 w-4 mr-2" />
                    External Contacts
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="team" className="space-y-4">
                  <QuickAssignCard
                    mode="mutation"
                    title="Sales PA Assignment"
                    description="Assign a Sales PA to this lead"
                    adjusters={allAdjusters}
                    roles={[{ role: "sales_pa", label: "Sales PA", testId: "qa-select-sales-pa", roleType: "sales_pa" }]}
                    paAssignments={leadPaAssignmentsData}
                    createPaAssignmentMutation={createPaAssignmentMutation}
                    updatePaAssignmentMutation={updatePaAssignmentMutation}
                    deletePaAssignmentMutation={deletePaAssignmentMutation}
                  />
                </TabsContent>

                <TabsContent value="external" className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    No external contacts assigned yet.
                  </p>
                  <Button className="w-full" variant="outline" data-testid="button-add-contact" onClick={() => toast({ title: "Coming Soon", description: "External contact management is under development." })}>
                    Add External Contact
                  </Button>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
              <CardDescription>
                Activity timeline including correspondence and updates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-4">
                  {activity && activity.length > 0 ? (
                    activity.map((item: any) => (
                      <div key={item.id} className="flex gap-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <MessageSquare className="h-4 w-4 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{item.type}</p>
                          <p className="text-sm text-muted-foreground">
                            by {item.user} • {new Date(item.createdAt).toLocaleDateString()}
                          </p>
                          <p className="text-sm mt-1">{item.description}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted-foreground">No activity yet</p>
                  )}
                </div>
              </ScrollArea>

              <div className="mt-4">
                <Button variant="outline" className="w-full" data-testid="button-comment" onClick={() => toast({ title: "Coming Soon", description: "Comment functionality is under development." })}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  New Comment
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                  <NotebookPen className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">Lead Notes</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Private notes visible only to your team
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={localNotes}
                onChange={(e) => setLocalNotes(e.target.value)}
                placeholder="Add context, observations, or follow-up reminders about this lead..."
                className="min-h-[300px] resize-none text-sm leading-relaxed"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {localNotes.length > 0
                    ? `${localNotes.length} character${localNotes.length === 1 ? '' : 's'}`
                    : 'No notes yet'}
                </span>
                <div className="flex items-center gap-3">
                  {localNotes !== (lead?.notes ?? '') && (
                    <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
                  )}
                  <Button
                    onClick={() => updateLeadMutation.mutate({ notes: localNotes })}
                    disabled={updateLeadMutation.isPending || localNotes === (lead?.notes ?? '')}
                    size="sm"
                  >
                    {updateLeadMutation.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        Save Notes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Convert to Claim Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent data-testid="dialog-convert">
          <DialogHeader>
            <DialogTitle>Convert Lead to Claim</DialogTitle>
            <DialogDescription>
              Choose the type of file to create from this lead
            </DialogDescription>
          </DialogHeader>
          
          {lead && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium">
                  {lead.claimType === 'commercial' ? (lead.company || 'Commercial Lead') : `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {lead.propertyAddress}
                </p>
                {lead.estimatedValue && (
                  <p className="text-xs text-muted-foreground">
                    Estimated Value: ${parseFloat(lead.estimatedValue).toLocaleString()}
                  </p>
                )}
              </div>
              
              <div className="space-y-3">
                <Label>Select File Type *</Label>
                <div className="space-y-3" role="radiogroup" aria-label="File type selection">
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-150 hover:scale-[1.01] hover:shadow-sm ${
                      conversionFileType === "public_adjuster"
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    }`}
                    role="radio"
                    aria-checked={conversionFileType === "public_adjuster"}
                    tabIndex={0}
                    onClick={() => setConversionFileType("public_adjuster")}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setConversionFileType("public_adjuster"); } }}
                    data-testid="option-public-adjuster"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                        conversionFileType === "public_adjuster" ? "border-primary" : "border-muted-foreground"
                      }`}>
                        {conversionFileType === "public_adjuster" && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">🛡️ PA File (Public Adjuster)</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          MPLC acts as the policyholder's representative throughout the entire claims process. 
                          Company fee typically ranges from 5% to 20% of settlement amount.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-150 hover:scale-[1.01] hover:shadow-sm ${
                      conversionFileType === "appraisal"
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    }`}
                    role="radio"
                    aria-checked={conversionFileType === "appraisal"}
                    tabIndex={0}
                    onClick={() => setConversionFileType("appraisal")}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setConversionFileType("appraisal"); } }}
                    data-testid="option-appraisal"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                        conversionFileType === "appraisal" ? "border-primary" : "border-muted-foreground"
                      }`}>
                        {conversionFileType === "appraisal" && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">📋 Appraisal File (We are Appraiser)</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          MPLC acts as the neutral appraiser in a dispute over scope or cost.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-150 hover:scale-[1.01] hover:shadow-sm ${
                      conversionFileType === "estimate"
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    }`}
                    role="radio"
                    aria-checked={conversionFileType === "estimate"}
                    tabIndex={0}
                    onClick={() => setConversionFileType("estimate")}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setConversionFileType("estimate"); } }}
                    data-testid="option-estimate"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                        conversionFileType === "estimate" ? "border-primary" : "border-muted-foreground"
                      }`}>
                        {conversionFileType === "estimate" && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">📊 Estimate</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          MPLC provides an independent damage estimate for the property loss.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-150 hover:scale-[1.01] hover:shadow-sm ${
                      conversionFileType === "umpire"
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    }`}
                    role="radio"
                    aria-checked={conversionFileType === "umpire"}
                    tabIndex={0}
                    onClick={() => setConversionFileType("umpire")}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setConversionFileType("umpire"); } }}
                    data-testid="option-umpire"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                        conversionFileType === "umpire" ? "border-primary" : "border-muted-foreground"
                      }`}>
                        {conversionFileType === "umpire" && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">⚖️ Umpire</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          MPLC serves as the neutral umpire to resolve a dispute between appraisers.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-150 hover:scale-[1.01] hover:shadow-sm ${
                      conversionFileType === "other"
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    }`}
                    role="radio"
                    aria-checked={conversionFileType === "other"}
                    tabIndex={0}
                    onClick={() => setConversionFileType("other")}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setConversionFileType("other"); } }}
                    data-testid="option-other"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                        conversionFileType === "other" ? "border-primary" : "border-muted-foreground"
                      }`}>
                        {conversionFileType === "other" && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">📂 Other</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Other file type not covered by the categories above.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConvertDialogOpen(false)}
              data-testid="button-cancel-convert"
            >
              Cancel
            </Button>
            <Button
              onClick={() => convertToClaimMutation.mutate(conversionFileType)}
              disabled={convertToClaimMutation.isPending}
              data-testid="button-confirm-convert"
            >
              {convertToClaimMutation.isPending ? "Converting\u2026" : "Convert to Claim"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={(open) => { if (!open) { setPreviewFile(null); setIsPdfLoading(false); } }}>
        <DialogContent className="w-screen h-screen max-w-none m-0 rounded-none flex flex-col" style={{ overscrollBehavior: 'contain' }} data-testid="dialog-file-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" aria-hidden="true" />
              {previewFile?.displayName || previewFile?.fileName || previewFile?.name || 'File Preview'}
            </DialogTitle>
            <DialogDescription>
              {previewFile?.fileType || 'Document'} — {previewFile?.uploadedAt ? `Uploaded ${dateFormatter.format(new Date(previewFile.uploadedAt))}` : 'Recently uploaded'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 rounded-lg overflow-hidden border bg-muted/30 relative">
            {isPdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Loading file...</span>
                </div>
              </div>
            )}
            {previewFile && (
              <iframe
                src={`/api/documents/${previewFile.id}/download?inline=true`}
                className="w-full h-full"
                title="File Preview"
                onLoad={() => setIsPdfLoading(false)}
              />
            )}
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
            <Button variant="outline" onClick={() => { setPreviewFile(null); setIsPdfLoading(false); }}>
              Close
            </Button>
            {previewFile?.driveWebViewLink && (
              <Button onClick={() => window.open(previewFile.driveWebViewLink, '_blank')}>
                <Download className="h-4 w-4 mr-2" />
                Open in Drive
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}